import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Palette, Type, Layers, Globe, MoveHorizontal } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import type {
  Slide,
  Theme,
} from "@/routes/~dashboard/~presentation/presentation-data";

interface StylesPanelProps {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  currentSlide: Slide;
  updateCurrentSlide: (slide: Slide) => void;
  allSlides: Slide[];
  updateAllSlides: (slides: Slide[]) => void;
}

export default function StylesPanel({
  theme,
  setTheme,
  currentSlide,
  updateCurrentSlide,
  allSlides,
  updateAllSlides,
}: StylesPanelProps) {
  const handleThemeChange = (key: keyof Theme, value: string) => {
    setTheme({
      ...theme,
      [key]: value,
    });

    const updatedAllSlides = allSlides.map((slide) => {
      const updatedSlide = { ...slide };

      if (key === "slideBackground") {
        updatedSlide.background = value;
      }

      if (key === "titleColor") {
        updatedSlide.titleColor = value;
      }

      if (key === "textColor") {
        updatedSlide.textColor = value;
      }

      return updatedSlide;
    });

    updateAllSlides(updatedAllSlides);
  };

  const handleSlideBackgroundChange = (value: string) => {
    updateCurrentSlide({
      ...currentSlide,
      background: value,
    });
  };

  const handleSlideTitleColorChange = (value: string) => {
    updateCurrentSlide({
      ...currentSlide,
      titleColor: value,
    });
  };

  const handleSlideTextColorChange = (value: string) => {
    updateCurrentSlide({
      ...currentSlide,
      textColor: value,
    });
  };

  return (
    <div className="space-y-6 p-4 text-secondary-foreground">
      <Tabs defaultValue="slide">
        <TabsList className="grid w-full grid-cols-2 bg-secondary">
          <TabsTrigger
            value="slide"
            className="text-secondary-foreground data-[state=active]:bg-accent"
          >
            <Layers className="mr-2 h-4 w-4" />
            Current Slide
          </TabsTrigger>
          <TabsTrigger
            value="theme"
            className="text-secondary-foreground data-[state=active]:bg-accent"
          >
            <Globe className="mr-2 h-4 w-4" />
            Global Style
          </TabsTrigger>
        </TabsList>

        <TabsContent value="slide" className="space-y-4 pt-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-secondary/50 rounded-lg border border-secondary p-3"
          >
            <div className="mb-3 flex items-center">
              <Palette className="mr-2 h-4 w-4 text-secondary-foreground" />
              <h3 className="text-sm font-medium text-secondary-foreground">
                Slide Appearance
              </h3>
            </div>
            <Separator className="my-2 bg-secondary" />
            <div className="mt-4 space-y-3">
              <Label
                htmlFor="slide-background"
                className="text-xs uppercase text-secondary-foreground"
              >
                Background Color
              </Label>
              <div className="flex items-center gap-2">
                <div
                  className="h-10 w-10 rounded-md"
                  style={{
                    backgroundColor:
                      currentSlide.background || theme.slideBackground,
                  }}
                />
                <Input
                  id="slide-background"
                  type="color"
                  value={currentSlide.background || theme.slideBackground}
                  onChange={(e) => handleSlideBackgroundChange(e.target.value)}
                  className="h-10 w-full cursor-pointer bg-background"
                />
              </div>
              <div className="mt-4 flex gap-2 p-2">
                {[
                  "#ffffff",
                  "#B7B1F2",
                  "#E69DB8",
                  "#FBF3B9",
                  "#A1EEBD",
                  "#7BD3EA",
                ].map((color) => (
                  <button
                    key={color}
                    onClick={() => handleSlideBackgroundChange(color)}
                    className="h-8 w-8 rounded-full border border-secondary shadow-md ring-accent transition-all hover:ring-2"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              <Label
                htmlFor="slide-title-color"
                className="text-xs uppercase text-secondary-foreground"
              >
                Title Color
              </Label>

              <div className="flex items-center gap-2">
                <div
                  className="h-10 w-10 rounded-md"
                  style={{
                    backgroundColor:
                      currentSlide.titleColor || theme.titleColor,
                  }}
                />
                <Input
                  id="slide-title-color"
                  type="color"
                  value={currentSlide.titleColor || theme.titleColor}
                  onChange={(e) => handleSlideTitleColorChange(e.target.value)}
                  className="h-10 w-full cursor-pointer bg-background"
                />
              </div>

              <div className="mt-4 flex gap-2 p-2">
                {[
                  "#ffffff",
                  "#402E7A",
                  "#570530",
                  "#E3651D",
                  "#1B4242",
                  "#03346E",
                ].map((color) => (
                  <button
                    key={color}
                    onClick={() => handleSlideTitleColorChange(color)}
                    className="h-8 w-8 rounded-full border border-secondary shadow-md ring-accent transition-all hover:ring-2"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              <Label
                htmlFor="slide-text-color"
                className="text-xs uppercase text-secondary-foreground"
              >
                Text Color
              </Label>
              <div className="flex items-center gap-2">
                <div
                  className="h-10 w-10 rounded-md"
                  style={{
                    backgroundColor: currentSlide.textColor || theme.textColor,
                  }}
                />
                <Input
                  id="slide-text-color"
                  type="color"
                  value={currentSlide.textColor || theme.textColor}
                  onChange={(e) => handleSlideTextColorChange(e.target.value)}
                  className="h-10 w-full cursor-pointer bg-background"
                />
              </div>

              <div className="mt-4 flex gap-2 p-2">
                {[
                  "#000000",
                  "#ffffff",
                  "#7D1935",
                  "#F39F5A",
                  "#78A083",
                  "#6EACDA",
                ].map((color) => (
                  <button
                    key={`text-${color}`}
                    onClick={() => handleSlideTextColorChange(color)}
                    className="h-8 w-8 rounded-full border border-secondary shadow-md ring-accent transition-all hover:ring-2"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </TabsContent>

        <TabsContent value="theme" className="space-y-6 pt-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-secondary/50 rounded-lg border border-secondary p-3"
          >
            <div className="mb-3 flex items-center">
              <Palette className="mr-2 h-4 w-4 text-secondary-foreground" />
              <h3 className="text-sm font-medium text-secondary-foreground">
                Colors
              </h3>
            </div>
            <Separator className="my-2 bg-secondary" />
            <div className="mt-3 space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="slide-background-global"
                  className="text-xs uppercase text-secondary-foreground"
                >
                  Default Background
                </Label>
                <div className="flex items-center gap-2">
                  <div
                    className="h-10 w-10 rounded-md border border-secondary shadow-inner"
                    style={{ backgroundColor: theme.slideBackground }}
                  />
                  <Input
                    id="slide-background-global"
                    type="color"
                    value={theme.slideBackground}
                    onChange={(e) =>
                      handleThemeChange("slideBackground", e.target.value)
                    }
                    className="h-10 w-full cursor-pointer border-secondary bg-background"
                  />
                </div>
                <div className="mt-4 flex flex-wrap gap-2 p-2">
                  {["#ffffff", "#B7B1F2", "#E69DB8", "#FBF3B9", "#A1EEBD"].map(
                    (color) => (
                      <button
                        key={color}
                        onClick={() =>
                          handleThemeChange("slideBackground", color)
                        }
                        className="h-8 w-8 rounded-full border border-secondary shadow-md ring-accent transition-all hover:ring-2"
                        style={{ backgroundColor: color }}
                      />
                    ),
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="title-color"
                  className="text-xs uppercase text-secondary-foreground"
                >
                  Title Color
                </Label>
                <div className="flex items-center gap-2">
                  <div
                    className="h-10 w-10 rounded-md shadow-inner"
                    style={{ backgroundColor: theme.titleColor }}
                  />
                  <Input
                    id="title-color"
                    type="color"
                    value={theme.titleColor}
                    onChange={(e) =>
                      handleThemeChange("titleColor", e.target.value)
                    }
                    className="h-10 w-full cursor-pointer border-secondary bg-background"
                  />
                </div>
                <div className="mt-4 flex flex-wrap gap-2 p-2">
                  {["#ffffff", "#402E7A", "#E3651D", "#1B4242", "#03346E"].map(
                    (color) => (
                      <button
                        key={color}
                        onClick={() => handleThemeChange("titleColor", color)}
                        className="h-8 w-8 rounded-full border border-secondary shadow-md ring-accent transition-all hover:ring-2"
                        style={{ backgroundColor: color }}
                      />
                    ),
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="text-color"
                  className="text-xs uppercase text-secondary-foreground"
                >
                  Text Color
                </Label>
                <div className="flex items-center gap-2">
                  <div
                    className="h-10 w-10 rounded-md border border-secondary shadow-inner"
                    style={{ backgroundColor: theme.textColor }}
                  />
                  <Input
                    id="text-color"
                    type="color"
                    value={theme.textColor}
                    onChange={(e) =>
                      handleThemeChange("textColor", e.target.value)
                    }
                    className="h-10 w-full cursor-pointer border-secondary bg-background"
                  />
                </div>
                <div className="mt-4 flex flex-wrap gap-2 p-2">
                  {["#000000", "#ffffff", "#F39F5A", "#78A083", "#6EACDA"].map(
                    (color) => (
                      <button
                        key={`text-${color}`}
                        onClick={() => handleThemeChange("textColor", color)}
                        className="h-8 w-8 rounded-full border border-secondary shadow-md ring-accent transition-all hover:ring-2"
                        style={{ backgroundColor: color }}
                      />
                    ),
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
            className="bg-secondary/50 rounded-lg border border-secondary p-3"
          >
            <div className="mb-3 flex items-center">
              <Type className="mr-2 h-4 w-4 text-white" />
              <h3 className="text-sm font-medium text-white">Typography</h3>
            </div>
            <Separator className="my-2 bg-secondary" />
            <div className="mt-3 space-y-4 text-black">
              <div className="space-y-2">
                <Label
                  htmlFor="font-family"
                  className="text-xs uppercase text-white"
                >
                  Font Family
                </Label>
                <Select
                  value={theme.fontFamily}
                  onValueChange={(value) =>
                    handleThemeChange("fontFamily", value)
                  }
                >
                  <SelectTrigger className="border-secondary bg-primary text-red-800">
                    <SelectValue placeholder="Select font" />
                  </SelectTrigger>
                  <SelectContent className="border-secondary bg-primary text-black">
                    <SelectItem value="'Inter', sans-serif">Inter</SelectItem>
                    <SelectItem value="'Roboto', sans-serif">Roboto</SelectItem>
                    <SelectItem value="'Playfair Display', serif">
                      Playfair Display
                    </SelectItem>
                    <SelectItem value="'Montserrat', sans-serif">
                      Montserrat
                    </SelectItem>
                    <SelectItem value="'Poppins', sans-serif">
                      Poppins
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
            className="bg-secondary/50 rounded-lg border border-secondary p-3"
          >
            <div className="mb-3 flex items-center">
              <MoveHorizontal className="mr-2 h-4 w-4 text-secondary-foreground" />
              <h3 className="text-sm font-medium text-secondary-foreground">
                Animations
              </h3>
            </div>
            <Separator className="my-2 bg-secondary" />
            <div className="mt-3 space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="transition"
                  className="text-xs uppercase text-secondary-foreground"
                >
                  Transition Style
                </Label>
                <Select
                  value={theme.transition}
                  onValueChange={(value) =>
                    handleThemeChange("transition", value)
                  }
                >
                  <SelectTrigger className="border-secondary bg-primary text-black">
                    <SelectValue placeholder="Select transition" />
                  </SelectTrigger>
                  <SelectContent className="border-secondary bg-primary text-black">
                    <SelectItem value="fade">Fade</SelectItem>
                    <SelectItem value="slide">Slide</SelectItem>
                    <SelectItem value="zoom">Zoom</SelectItem>
                    <SelectItem value="none">None</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
